import { z } from "zod";
import type { Operation } from "../types";

/**
 * Market Intelligence — ICP profile CRUD (docs/market/PRD.md). The `/market`
 * ICP hub renders profiles read-only; this contract is the user-facing seam to
 * create, edit, toggle-primary, and archive an ICP segment. The data layer
 * (excellent/src/market/db.ts createIcpProfile/updateIcpProfile/archiveIcpProfile)
 * already emits the Normal-Core events + materializes the projection — the route
 * just calls it. Tenant-scoped: the route resolves the workspace, never the client.
 *
 * The DTO mirrors `MarketIcpProfileRow` (the UI layer can't import the market db
 * module — server-only). `is_primary` surfaces as the row's integer (0|1); the
 * write ops take a boolean.
 */

const icpProfileDTO = z.object({
  id: z.number(),
  uid: z.string().nullable(),
  workspace_id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  firmographics: z.string().nullable(),
  pains: z.string().nullable(),
  buying_triggers: z.string().nullable(),
  disqualifiers: z.string().nullable(),
  channels: z.string().nullable(),
  is_primary: z.number(),
  source: z.enum(["manual", "onboarding", "analysis"]),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type IcpProfileDTO = z.infer<typeof icpProfileDTO>;

// ---- List (GET) ----
const listResponse = z.object({ profiles: z.array(icpProfileDTO) });
export type IcpListResponse = z.infer<typeof listResponse>;

const listOp: Operation<void, void, void, IcpListResponse> = {
  method: "GET",
  path: () => "/api/market/icp",
  response: listResponse,
};

// ---- Create (POST) ----
// The editable field set — every column the "Define a segment" walk + the edit
// surface touch. Only `name` is required; the rest are nullable free-text plus
// the primary flag.
const createBody = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  firmographics: z.string().nullable().optional(),
  pains: z.string().nullable().optional(),
  buying_triggers: z.string().nullable().optional(),
  disqualifiers: z.string().nullable().optional(),
  channels: z.string().nullable().optional(),
  is_primary: z.boolean().optional(),
});
export type IcpCreateBody = z.infer<typeof createBody>;

const mutateResponse = z.object({ profile: icpProfileDTO });
export type IcpMutateResponse = z.infer<typeof mutateResponse>;

const createOp: Operation<void, void, IcpCreateBody, IcpMutateResponse> = {
  method: "POST",
  path: () => "/api/market/icp",
  body: createBody,
  response: mutateResponse,
  // inserts (and may flip is_primary on siblings) — whole list family.
  invalidates: [listOp],
};

// ---- Update (PATCH) ----
// A partial patch keyed by the profile id. `is_primary` is included so the edit
// surface can promote a segment to primary; the db layer normalizes the flag.
const updateBody = z.object({
  id: z.number(),
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  firmographics: z.string().nullable().optional(),
  pains: z.string().nullable().optional(),
  buying_triggers: z.string().nullable().optional(),
  disqualifiers: z.string().nullable().optional(),
  channels: z.string().nullable().optional(),
  is_primary: z.boolean().optional(),
});
export type IcpUpdateBody = z.infer<typeof updateBody>;

const updateResponse = z.object({ ok: z.boolean() });
export type IcpUpdateResponse = z.infer<typeof updateResponse>;

const updateOp: Operation<void, void, IcpUpdateBody, IcpUpdateResponse> = {
  method: "PATCH",
  path: () => "/api/market/icp",
  body: updateBody,
  response: updateResponse,
  // name/fields/is_primary all live on list rows; promote-primary flips peers.
  invalidates: [listOp],
};

// ---- Archive (DELETE) ----
const archiveBody = z.object({ id: z.number() });
export type IcpArchiveBody = z.infer<typeof archiveBody>;

const archiveOp: Operation<void, void, IcpArchiveBody, IcpUpdateResponse> = {
  method: "DELETE",
  path: () => "/api/market/icp",
  body: archiveBody,
  response: updateResponse,
  // soft-archive removes the row from the active list.
  invalidates: [listOp],
};

export const marketIcp = {
  list: listOp,
  create: createOp,
  update: updateOp,
  archive: archiveOp,
};
